
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, FilePlus, Printer } from "lucide-react";
import { PatientFile, FileExportFormat, usePatientFileExport } from "./usePatientFileExport";
import { useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface ViewPatientFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: PatientFile;
}

export const ViewPatientFileDialog = ({ open, onOpenChange, file }: ViewPatientFileDialogProps) => {
  const { exportToPDF, exportToCSV, isExporting } = usePatientFileExport();
  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (contentRef.current) {
      const printWindow = window.open('', '', 'height=600,width=800');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Dossier Patient</title>');
        // Add some basic styles for printing
        printWindow.document.write(`<style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 10px; }
          .section { margin-bottom: 20px; }
          .section-title { font-weight: bold; margin-bottom: 5px; color: #555; }
          .data-row { display: flex; margin-bottom: 5px; }
          .label { font-weight: bold; width: 180px; }
          .value { flex: 1; }
        </style>`);
        printWindow.document.write('</head><body>');
        printWindow.document.write(contentRef.current.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Dossier patient - {file.file_number}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={isExporting}
              >
                <Printer className="h-4 w-4 mr-2" />
                Imprimer
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isExporting}>
                    <Download className="h-4 w-4 mr-2" />
                    Exporter
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => exportToCSV(file)}>
                    <FileText className="h-4 w-4 mr-2" />
                    <span>CSV</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportToPDF(file, "standard")}>
                    <FilePlus className="h-4 w-4 mr-2" />
                    <span>PDF Standard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportToPDF(file, "detailed")}>
                    <FilePlus className="h-4 w-4 mr-2" />
                    <span>PDF Détaillé</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[calc(90vh-100px)] pr-4">
          <div ref={contentRef} className="space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl font-bold border-b pb-2">Informations personnelles</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <div className="text-sm font-medium text-gray-500">Nom</div>
                  <div>{file.last_name}</div>
                </div>
                
                <div>
                  <div className="text-sm font-medium text-gray-500">Prénom</div>
                  <div>{file.first_name}</div>
                </div>
                
                {file.birth_date && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Date de naissance</div>
                    <div>{format(new Date(file.birth_date), 'PPP', { locale: fr })}</div>
                  </div>
                )}
                
                {file.gender && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Sexe</div>
                    <div>{file.gender === 'M' ? 'Masculin' : file.gender === 'F' ? 'Féminin' : file.gender}</div>
                  </div>
                )}
                
                {file.marital_status && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Situation familiale</div>
                    <div>{file.marital_status}</div>
                  </div>
                )}
                
                {file.children_count !== undefined && file.children_count !== null && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Nombre d'enfants</div>
                    <div>{file.children_count}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <h2 className="text-xl font-bold border-b pb-2">Informations de contact</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {file.email && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Email</div>
                    <div>{file.email}</div>
                  </div>
                )}
                
                {file.phone_number && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Téléphone</div>
                    <div>{file.phone_number}</div>
                  </div>
                )}
                
                {file.address && (
                  <div className="sm:col-span-2">
                    <div className="text-sm font-medium text-gray-500">Adresse</div>
                    <div>{file.address}</div>
                    {(file.postal_code || file.city) && (
                      <div>
                        {file.postal_code && file.postal_code}{" "}
                        {file.city && file.city}
                      </div>
                    )}
                    {file.country && <div>{file.country}</div>}
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <h2 className="text-xl font-bold border-b pb-2">Informations médicales</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {file.height && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Taille</div>
                    <div>{file.height} cm</div>
                  </div>
                )}
                
                {file.weight && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Poids</div>
                    <div>{file.weight} kg</div>
                  </div>
                )}
                
                {file.blood_type && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Groupe sanguin</div>
                    <div>{file.blood_type}</div>
                  </div>
                )}
                
                {file.treating_doctor && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Médecin traitant</div>
                    <div>{file.treating_doctor}</div>
                  </div>
                )}
                
                <div>
                  <div className="text-sm font-medium text-gray-500">Fumeur</div>
                  <div>{file.smoker ? 'Oui' : 'Non'}</div>
                </div>
                
                {file.smoker && file.cigarettes_per_day && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Cigarettes par jour</div>
                    <div>{file.cigarettes_per_day}</div>
                  </div>
                )}
                
                {file.allergies && (
                  <div className="sm:col-span-2">
                    <div className="text-sm font-medium text-gray-500">Allergies</div>
                    <div>{file.allergies}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <h2 className="text-xl font-bold border-b pb-2">Assurance</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {file.social_security_number && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Numéro de sécurité sociale</div>
                    <div>{file.social_security_number}</div>
                  </div>
                )}
                
                {file.insurance_provider && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Mutuelle</div>
                    <div>{file.insurance_provider}</div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Affichage des notes */}
            {file.notes && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold border-b pb-2">Notes</h2>
                <div className="whitespace-pre-wrap bg-muted/20 p-3 rounded-md">
                  {file.notes}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
